/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { decorate, observable, action, runInAction } from 'mobx';
import { Button, Header, Container, Message } from 'semantic-ui-react';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import Form from '@amzn/base-ui/dist/parts/helpers/fields/Form';
import Input from '@amzn/base-ui/dist/parts/helpers/fields/Input';
import TextArea from '@amzn/base-ui/dist/parts/helpers/fields/TextArea';

import { getAddUserApplicationForm } from '../models/forms/AddUserApplicationForm';

class UserApplication extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};

    runInAction(() => {
      this.logoutProcessing = false;
      this.currentUser = props.userStore.cloneUser;
    });
    this.form = getAddUserApplicationForm();
    this.form.$('email').value = this.currentUser.username;
  }

  handleLogout = action(async event => {
    this.logoutProcessing = true;
    event.preventDefault();
    event.stopPropagation();

    try {
      await this.props.authentication.logout();
      runInAction(() => {
        this.logoutProcessing = false;
      });
    } catch (error) {
      displayError(error);
      runInAction(() => {
        this.logoutProcessing = false;
      });
    }
  });

  render() {
    let content = null;
    if (this.currentUser.status === 'pending') {
      content = this.renderFormSubmittedMessage();
    } else {
      // content = this.renderSignUpMessage();
      content = this.renderSignUpBanana();
    }
    return content;
  }

  renderAddUserPage() {
    return (
      <Container className="pt4">
        <div className="mt2 animated fadeIn">
          <Header as="h2" icon textAlign="center" className="mt3" color="grey">
            Welcome to RACE!
          </Header>
          <Header as="h3" icon textAlign="center" className="mt3" color="grey">
            To obtain access, please contact race@rmit.edu.au
          </Header>
          <div className="mt3 ml3 mr3 animated fadeIn">{this.renderAddUserForm()}</div>
        </div>
      </Container>
    );
  }

  renderFormSubmittedMessage() {
    const processing = this.logoutProcessing;
    return (
      <Container text className="pt4">
        <Message icon>
          <Message.Content>
            <Message.Header>We have received your application</Message.Header>
            You will not have access to the portal until you contact us at race@rmit.edu.au.
            <div className="mt2">
              We recommend you logout and login when you have received access.
              <Button
                floated="right"
                color="blue"
                icon
                disabled={processing}
                className="ml2"
                onClick={this.handleLogout}
              >
                Logout
              </Button>
            </div>
          </Message.Content>
        </Message>
      </Container>
    );
  }

  renderSignUpMessage() {
    const processing = this.logoutProcessing;
    return (
      <Container text className="pt4">
        <Message icon>
          <Message.Content>
            <Message.Header>You're one step closer to harnessing the power of the cloud!</Message.Header>
              <p>If signing up per the RACE Team's request, please logout. A team member will process your request soon.</p>
              <p>If you would like to request access, please logout and select 'Request Access' instead.</p>
              <p>Have a query? Please contact us at <u>race@rmit.edu.au</u>.</p>
            <div className="mt2">
              Please logout and login once you have received access.
              <Button
                floated="right"
                color="blue"
                icon
                disabled={processing}
                className="ml2"
                onClick={this.handleLogout}
              >
                Logout
              </Button>
            </div>
          </Message.Content>
        </Message>
      </Container>
    );
  }

  // banana is the icon where the style come from ;)
  renderSignUpBanana() {
    return (
      <div className="sign-up-banana-container">
        <div className="sign-up-banana">
          <div className="banana-content">
            <img className="logo ardc" src={this.props.assets.images.ardcLogo} alt="ARDC Logo" />
            <div className="banana-texts">
              <div className="title">Your account is being provisioned</div>
              <div className="description">You will receive an email once your account credit has been applied. For any inquiries, you can contact RACE anytime at race@rmit.edu.au, or via the button below.</div>
            </div>
            <div className="banana-buttons">
              <a href="mailto:race+ads@rmit.edu.au?subject=Australian%20Dataspaces%20Prototype%20Access%20Request&body=Name%3A%20%0ACompany%3A%20%0ARole%3A%20%0AReason%20for%20Access%3A%20" className="banana-btn primary">
                Contact RACE
              </a>
              <div className="banana-btn secondary" onClick={this.handleLogout}>
                Logout
              </div>
            </div>
            <img className="logo race" src={this.props.assets.images.poweredByRACEWide1} alt="RACE Logo" />
          </div>
          <div className="banana-image">
            <img alt="Cloud researcher" src={this.props.assets.images.CloudResearcher} />
          </div>
        </div>
      </div>
    );
  }

  renderAddUserForm() {
    const form = this.form;

    return (
      <Form form={form} onSuccess={this.handleSubmit}>
        {({ processing }) => (
          <>
            <Input field={form.$('email')} disabled className="mt3 mb4" />
            <Input field={form.$('firstName')} className="mt3 mb4" />
            <Input field={form.$('lastName')} />
            <TextArea field={form.$('applyReason')} />

            <Button className="ml2 mb3" floated="right" color="blue" icon disabled={processing} type="submit">
              Submit Application
            </Button>
          </>
        )}
      </Form>
    );
  }

  handleSubmit = async form => {
    try {
      const user = form.values();

      this.currentUser.firstName = user.firstName;
      this.currentUser.lastName = user.lastName;
      this.currentUser.applyReason = user.applyReason;
      this.currentUser.status = 'pending';
      const updatedUser = await this.getStore().updateUserApplication(this.currentUser);
      runInAction(() => {
        this.currentUser.status = updatedUser.status;
      });
    } catch (error) {
      displayError(error);
    }
  };

  getStore() {
    return this.props.usersStore;
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(UserApplication, {
  logoutProcessing: observable,
  currentUser: observable,
});

export default inject(
  'userStore',
  'usersStore',
  'userRolesStore',
  'awsAccountsStore',
  'indexesStore',
  'authentication',
  'assets',
)(withRouter(observer(UserApplication)));
